import { useState } from 'react';
import { Link } from 'react-router-dom';

import type { MoneyWire } from '@rheora/enrollment-contract';

import { useTranslation } from '@fluxora/i18n';
import { Alert, Button, Input, Label } from '@fluxora/ui';

import { useCardOnFileMutation } from '../api/deposit.queries';
import { deriveMaskedCard, type MaskedCardErrors } from '../services/card.service';
import { detectBrand, formatCardNumber, formatExpiry, maxDigits } from '../services/cardEntry';
import { formatMoney } from '../services/credit.service';

/**
 * No-Stripe "card on file" form (E-SF05). Captures the card, DERIVES the masked
 * descriptor in the browser, and sends ONLY `{ brand, last4, expMonth, expYear }`
 * to `/bff/deposit/card-on-file`.
 *
 * ── THE SAME ADD-CARD AS THE PORTAL AND THE CRM ─────────────────────────────
 * Same fields, same checks, same messages as the Customer Portal's Add card and
 * the CRM's Payment methods panel (`cardEntry.ts`): the number off the front of
 * the card, the network read from it as it is typed, and the expiry. When the
 * enrollment is signed, Quotation's signature worker registers this card with
 * billing (`POST /v1/us/payment-methods`, the CRM's own call) on the new account,
 * so it is a card the CRM can charge and the portal can show — not a copy that
 * only this funnel knows about.
 *
 * ── PCI BOUNDARY ────────────────────────────────────────────────────────────
 * The full card number NEVER leaves the browser: on submit we compute the masked
 * descriptor and IMMEDIATELY clear the raw number. There is no CVV field. It used
 * to be collected "so the form looks real" and then thrown away, which taught the
 * customer we keep it; neither the portal nor the CRM asks for one. (Deriving
 * `last4` means our JS briefly sees the PAN, unlike Stripe's iframe — see
 * card.service.ts.)
 */

export interface CardOnFileFormProps {
  readonly candidateId: string;
  readonly amount: MoneyWire;
  /**
   * True when `amount` is a PLACEHOLDER, not an individually-determined §25.478
   * figure — the submit label then says "estimated deposit" rather than naming it
   * as the charge. A PROP rather than another read of the env so this form and the
   * banner above it can never disagree about the same number
   * (`deposit-disclosure.service.ts`).
   */
  readonly amountIsProvisional: boolean;
  /** Forward link to the estimate, shown once the card is on file. */
  readonly toEstimate: string;
  /** Fired the moment the masked card stores — the parent opens its popup. */
  readonly onSaved?: () => void;
}

export function CardOnFileForm({
  candidateId,
  amount,
  amountIsProvisional,
  toEstimate,
  onSaved,
}: CardOnFileFormProps): JSX.Element {
  const { t } = useTranslation();
  const cardOnFile = useCardOnFileMutation();

  const [number, setNumber] = useState('');
  const [expiry, setExpiry] = useState('');
  const [errors, setErrors] = useState<MaskedCardErrors>({});

  // The network is knowable from the first digit or two, long before the number
  // is complete — so the brand field follows `detectBrand`, not the full check.
  // Watching "Visa" appear as you type the 4 is the feedback that the field is
  // reading what you meant.
  const detected = detectBrand(number);

  const submit = (): void => {
    // `deriveMaskedCard` is a PURE service (card.service.ts) — the only place that
    // reads the raw digits, and it returns ONLY the masked descriptor.
    const masked = deriveMaskedCard(number, expiry);
    if ('errors' in masked) {
      setErrors(masked.errors);
      return;
    }
    setErrors({});
    // Discard the raw PAN the instant we hold the masked descriptor — it is never
    // sent and never kept.
    setNumber('');
    cardOnFile.mutate({ candidateId, card: masked }, { onSuccess: () => onSaved?.() });
  };

  if (cardOnFile.isSuccess) {
    const card = cardOnFile.data.paymentMethod;
    return (
      <div className="flex flex-col gap-3" data-testid="card-on-file-success">
        <Alert variant="success" title={t('shop.deposit.card.savedTitle')} role="status">
          {t('shop.deposit.card.savedBody', {
            brand: card.brand,
            last4: card.last4,
            exp: `${String(card.expMonth).padStart(2, '0')}/${String(card.expYear)}`,
          })}
        </Alert>
        <div className="flex justify-center">
          <Link to={toEstimate}>
            <Button variant="primary" shape="pill" className="cta-sheen">
              {t('shop.credit.continueToEstimate')}
            </Button>
          </Link>
        </div>
      </div>
    );
  }

  return (
    <div className="flex flex-col gap-3">
      <div className="flex flex-col gap-3 rounded-[14px] border border-border bg-surface p-4">
        <div className="flex flex-col gap-1">
          <Label htmlFor="cof-number">{t('shop.deposit.card.number')}</Label>
          <Input
            id="cof-number"
            placeholder="1234 5678 9012 3456"
            inputMode="numeric"
            autoComplete="cc-number"
            value={number}
            hasError={errors.number !== undefined}
            aria-describedby={errors.number !== undefined ? 'cof-number-error' : undefined}
            onChange={(e) => setNumber(formatCardNumber(e.target.value))}
            // Digits for the network's longest PAN, plus the spaces between groups.
            maxLength={maxDigits(number) + 4}
          />
          {errors.number !== undefined ? (
            <p id="cof-number-error" role="alert" className="text-xs text-danger">
              {errors.number}
            </p>
          ) : null}
        </div>
        <div className="grid grid-cols-2 gap-3">
          <div className="flex flex-col gap-1">
            <Label htmlFor="cof-exp">{t('shop.deposit.card.expLabel')}</Label>
            <Input
              id="cof-exp"
              placeholder={t('shop.deposit.card.expPlaceholder')}
              inputMode="numeric"
              autoComplete="cc-exp"
              value={expiry}
              hasError={errors.expiry !== undefined}
              aria-describedby={errors.expiry !== undefined ? 'cof-exp-error' : undefined}
              onChange={(e) => setExpiry(formatExpiry(e.target.value))}
              maxLength={5}
            />
            {errors.expiry !== undefined ? (
              <p id="cof-exp-error" role="alert" className="text-xs text-danger">
                {errors.expiry}
              </p>
            ) : null}
          </div>
          <div className="flex flex-col gap-1">
            <Label htmlFor="cof-brand">{t('shop.deposit.card.brand')}</Label>
            {/* Read-only, and that is the point: the brand is one of the facts this
                form stores, so the customer sees what was read off their number
                before agreeing to save it. */}
            <Input id="cof-brand" value={detected ?? ''} readOnly tabIndex={-1} />
          </div>
        </div>
      </div>
      {cardOnFile.isError ? (
        <Alert variant="danger" role="alert">
          {t('shop.deposit.card.error')}
        </Alert>
      ) : null}

      {/* ⚠️ THE BUTTON NAMES THE MONEY, so it needs the same honesty as the banner:
          "Save card · $400.00 deposit" reads as the charge being set up. When the
          figure is provisional the label says so. */}
      <Button variant="primary" onClick={submit} disabled={cardOnFile.isPending}>
        {cardOnFile.isPending
          ? t('shop.deposit.card.saving')
          : amountIsProvisional
            ? t('shop.deposit.card.save.provisional', { amount: formatMoney(amount) })
            : t('shop.deposit.card.save', { amount: formatMoney(amount) })}
      </Button>
    </div>
  );
}
